import { useEffect, useState } from 'react';
import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { isSidebarOpsPoIndependentType } from '../constants/sidebarOpsPoHygieneExemptions';
import {
  batchResolvePoActiveForSidebar,
  type SidebarPartyField,
} from '../services/sidebarOpsNotificationHygiene';
import { markNotificationDocsAsRead } from '../services/notificationTrackingService';
import Logger from '../services/logger';

interface SidebarHygieneNotification {
  id: string;
  poNumber?: string;
  type?: string;
  read?: boolean;
}

/**
 * Resolves PO actionability for sidebar dot counts and auto-marks read notifications
 * tied to terminal / missing POs so they do not return on the next sign-in.
 */
export function useSidebarOpsNotificationHygiene(
  notifications: SidebarHygieneNotification[],
  userId: string | undefined,
  partyField: SidebarPartyField
): Map<string, boolean> {
  const [poActiveMap, setPoActiveMap] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (!userId) {
      setPoActiveMap(new Map());
      return;
    }

    const poNumbers = notifications
      .filter((n) => n.poNumber && n.read !== true)
      .map((n) => String(n.poNumber));

    if (poNumbers.length === 0) {
      setPoActiveMap(new Map());
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const map = await batchResolvePoActiveForSidebar(
          getFailoverAwareDatabase(),
          userId,
          poNumbers,
          partyField
        );
        if (cancelled) return;

        setPoActiveMap(map);

        const staleIds = notifications
          .filter(
            (n) =>
              n.poNumber &&
              map.get(String(n.poNumber)) === false &&
              n.read !== true &&
              !isSidebarOpsPoIndependentType(n.type)
          )
          .map((n) => n.id);

        if (staleIds.length > 0) {
          await markNotificationDocsAsRead(staleIds);
        }
      } catch (err) {
        Logger.warn('[useSidebarOpsNotificationHygiene] Failed to resolve PO actionability', {
          err,
          partyField,
          userId,
        });
        if (!cancelled) {
          setPoActiveMap(new Map());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notifications, userId, partyField]);

  return poActiveMap;
}
